// ============================================
// FILE: /frontend/src/pages/admin/FinanceApplicationsAdminPage.jsx
// PURPOSE: Admin dashboard to view and manage finance applications
// ============================================

import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/useToast';
import {
  Loader2,
  Eye,
  FileText,
  Search,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import {
  getAllApplications,
  getApplicationById,
  updateApplicationStatus,
  downloadApplicationPDF
} from '../../api/finance.api';
import { format } from 'date-fns';

// ========================================
// FINANCE TYPE OPTIONS
// ========================================
const FINANCE_TYPES = [
  'Asset Finance',
  'Mortgage Finance',
  'Construction Finance',
  'Islamic Finance: Asset Finance',
  'Islamic Finance: Mortgage Finance',
  'Insurance Premium Financing'
];

const STATUS_OPTIONS = ['PENDING', 'APPROVED', 'REJECTED'];

// ========================================
// MAIN COMPONENT
// ========================================
export default function FinanceApplicationsAdminPage() {
  console.log('[FinanceAdminPage] 🎨 Component mounted');

  const { toast } = useToast();

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    financeType: '',
    search: ''
  });

  // Status update form
  const [statusForm, setStatusForm] = useState({
    status: '',
    adminNotes: ''
  });

  // ========================================
  // FETCH APPLICATIONS
  // ========================================
  useEffect(() => {
    fetchApplications();
  }, [filters.status, filters.financeType]);

  const fetchApplications = async () => {
    console.log('[FinanceAdminPage] 📋 Fetching applications');
    console.log('[FinanceAdminPage] Filters:', filters);

    setLoading(true);

    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.financeType) params.financeType = filters.financeType;

      const response = await getAllApplications(params);
      console.log('[FinanceAdminPage] ✅ Applications fetched:', response.data.length);
      setApplications(response.data);
    } catch (error) {
      console.error('[FinanceAdminPage] ❌ Error fetching applications:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load applications'
      });
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // VIEW APPLICATION DETAILS
  // ========================================
  const handleViewDetails = async (applicationId) => {
    console.log('[FinanceAdminPage] 👁️ Viewing application:', applicationId);

    try {
      const response = await getApplicationById(applicationId);
      console.log('[FinanceAdminPage] ✅ Application loaded');
      setSelectedApplication(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('[FinanceAdminPage] ❌ Error loading application:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load application details'
      });
    }
  };

  // ========================================
  // OPEN STATUS UPDATE MODAL
  // ========================================
  const handleOpenStatusModal = (application) => {
    console.log('[FinanceAdminPage] 🔄 Opening status modal for:', application.id);
    setSelectedApplication(application);
    setStatusForm({
      status: application.status,
      adminNotes: application.adminNotes || ''
    });
    setShowStatusModal(true);
  };

  // ========================================
  // UPDATE APPLICATION STATUS
  // ========================================
  const handleUpdateStatus = async () => {
    console.log('[FinanceAdminPage] 🔄 Updating status');
    console.log('[FinanceAdminPage] New status:', statusForm.status);

    setUpdatingStatus(true);

    try {
      await updateApplicationStatus(
        selectedApplication.id,
        statusForm.status,
        statusForm.adminNotes
      );

      console.log('[FinanceAdminPage] ✅ Status updated successfully');

      toast({
        title: 'Success',
        description: 'Application status updated successfully'
      });

      setShowStatusModal(false);
      fetchApplications();
    } catch (error) {
      console.error('[FinanceAdminPage] ❌ Error updating status:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update status'
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ========================================
  // DOWNLOAD PDF
  // ========================================
  const handleDownloadPDF = async (applicationId) => {
    console.log('[FinanceAdminPage] 📄 Downloading PDF:', applicationId);

    try {
      const blob = await downloadApplicationPDF(applicationId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `finance-application-${applicationId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('[FinanceAdminPage] ✅ PDF downloaded');

      toast({
        title: 'Downloaded',
        description: 'PDF downloaded successfully'
      });
    } catch (error) {
      console.error('[FinanceAdminPage] ❌ Download error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to download PDF'
      });
    }
  };

  // ========================================
  // FILTER APPLICATIONS
  // ========================================
  const filteredApplications = applications.filter((app) => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        app.fullName.toLowerCase().includes(searchLower) ||
        app.email.toLowerCase().includes(searchLower) ||
        app.phone.includes(filters.search)
      );
    }
    return true;
  });

  // ========================================
  // STATUS BADGE
  // ========================================
  const getStatusBadge = (status) => {
    const variants = {
      PENDING: 'default',
      APPROVED: 'success',
      REJECTED: 'destructive'
    };

    const icons = {
      PENDING: Clock,
      APPROVED: CheckCircle,
      REJECTED: XCircle
    };

    const Icon = icons[status];

    return (
      <Badge variant={variants[status]} className="text-[10px] sm:text-xs">
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    console.log('[FinanceAdminPage] ⏳ Loading...');
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // ========================================
  // RENDER
  // ========================================
  console.log('[FinanceAdminPage] 🎨 Rendering with', filteredApplications.length, 'applications');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* HEADER */}
      <div>
        <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">
          Finance Applications
        </h1>
        <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
          Manage and review finance applications
        </p>
      </div>

      {/* FILTERS */}
      <Card className="bg-muted/40">
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="text-sm sm:text-base md:text-lg flex items-center gap-2">
            <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-xs sm:text-sm">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Name, email, phone..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9 text-xs sm:text-sm h-9 sm:h-10"
                />
              </div>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-xs sm:text-sm">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(val) => setFilters({ ...filters, status: val })}
              >
                <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" " className="text-xs sm:text-sm">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status} className="text-xs sm:text-sm">
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-xs sm:text-sm">Finance Type</Label>
              <Select
                value={filters.financeType}
                onValueChange={(val) => setFilters({ ...filters, financeType: val })}
              >
                <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" " className="text-xs sm:text-sm">All Types</SelectItem>
                  {FINANCE_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="text-xs sm:text-sm">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* APPLICATIONS TABLE */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">Applicant</TableHead>
                  <TableHead className="text-xs sm:text-sm">Finance Type</TableHead>
                  <TableHead className="text-xs sm:text-sm">Amount</TableHead>
                  <TableHead className="text-xs sm:text-sm">Status</TableHead>
                  <TableHead className="text-xs sm:text-sm">Date</TableHead>
                  <TableHead className="text-xs sm:text-sm text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApplications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-xs sm:text-sm text-muted-foreground py-8">
                      No applications found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApplications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell className="text-xs sm:text-sm">
                        <div>
                          <div className="font-medium">{app.fullName}</div>
                          <div className="text-muted-foreground text-[10px] sm:text-xs">{app.email}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs sm:text-sm">{app.financeType}</TableCell>
                      <TableCell className="text-xs sm:text-sm font-medium">
                        KES {parseFloat(app.loanAmount).toLocaleString()}
                      </TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell className="text-xs sm:text-sm">
                        {format(new Date(app.createdAt), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(app.id)}
                            className="text-xs h-8"
                          >
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPDF(app.id)}
                            className="text-xs h-8"
                          >
                            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* DETAILS MODAL */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Application Details</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              View complete application information
            </DialogDescription>
          </DialogHeader>

          {selectedApplication && (
            <div className="space-y-4 sm:space-y-6">
              {/* Personal Info */}
              <div>
                <h3 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">Personal Information</h3>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">{selectedApplication.fullName}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <p className="font-medium">{selectedApplication.email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <p className="font-medium">{selectedApplication.phone}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Income Source:</span>
                    <p className="font-medium">{selectedApplication.sourceOfIncome}</p>
                  </div>
                </div>
              </div>

              {/* Finance Details */}
              <div>
                <h3 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">Finance Details</h3>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                  <div>
                    <span className="text-muted-foreground">Finance Type:</span>
                    <p className="font-medium">{selectedApplication.financeType}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Loan Amount:</span>
                    <p className="font-medium">KES {parseFloat(selectedApplication.loanAmount).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Repayment Period:</span>
                    <p className="font-medium">{selectedApplication.repaymentPeriod}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <div className="mt-1">{getStatusBadge(selectedApplication.status)}</div>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedApplication.adminNotes && (
                <div>
                  <h3 className="text-sm sm:text-base font-semibold mb-2">Admin Notes</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {selectedApplication.adminNotes}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={() => handleOpenStatusModal(selectedApplication)}
                  className="flex-1 text-xs sm:text-sm h-9 sm:h-10"
                >
                  Update Status
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPDF(selectedApplication.id)}
                  className="flex-1 text-xs sm:text-sm h-9 sm:h-10"
                >
                  <FileText className="mr-2 w-4 h-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* STATUS UPDATE MODAL */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Update Application Status</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Change the status and add notes
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-xs sm:text-sm">Status *</Label>
              <Select
                value={statusForm.status}
                onValueChange={(val) => setStatusForm({ ...statusForm, status: val })}
              >
                <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status} className="text-xs sm:text-sm">
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-xs sm:text-sm">Admin Notes</Label>
              <Textarea
                placeholder="Add notes about this decision..."
                value={statusForm.adminNotes}
                onChange={(e) => setStatusForm({ ...statusForm, adminNotes: e.target.value })}
                className="text-xs sm:text-sm min-h-[80px]"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowStatusModal(false)}
              className="text-xs sm:text-sm h-9 sm:h-10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateStatus}
              disabled={updatingStatus}
              className="text-xs sm:text-sm h-9 sm:h-10"
            >
              {updatingStatus ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Status'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}